import ApiService from '@/core/services/ApiService';
import { Actions } from '@/store/enums/StoreEnums';
import { Module, Action, VuexModule } from 'vuex-module-decorators';

export interface DeeplinkDestination {
  destination: string;
  screen: string;
  required: Array<string>;
  optional: Array<string>;
}

export interface DeeplinkNotificationPayload {
  deeplink: string;
  titleAr: string;
  titleEn: string;
  bodyAr: string;
  bodyEn: string;
  audience: 'members' | 'all';
  memberIds?: Array<number>;
  imageUrl?: string;
}

export interface DeeplinkSendResult {
  status: 'sent' | 'queued';
  recipients: number;
  sent?: number;
  failed?: number;
}

export interface DeeplinkMember {
  id: number;
  name: string;
  phone: string;
  email: string;
}

export interface DeeplinkApiResult<T> {
  data: T | null;
  status?: number;
  message?: string;
}

const toApiError = (error): DeeplinkApiResult<never> => {
  const message =
    error?.response?.data?.message ?? error?.message ?? 'SOMETHING_WENT_WRONG';
  return {
    data: null,
    status: error?.response?.status,
    message: Array.isArray(message) ? message.join(', ') : message,
  };
};

@Module
export default class DeeplinkNotificationsModule extends VuexModule {
  @Action
  [Actions.GET_DEEPLINK_DESTINATIONS](): Promise<
    DeeplinkApiResult<Array<DeeplinkDestination>>
  > {
    ApiService.setAuthorizationHeader();
    return ApiService.query('/deeplink-notifications/destinations', {})
      .then(({ data }) => ({ data: data.data }))
      .catch(toApiError);
  }

  @Action
  [Actions.GET_DEEPLINK_LOOKUP](
    resource: string
  ): Promise<DeeplinkApiResult<Array<Record<string, unknown>>>> {
    ApiService.setAuthorizationHeader();
    return ApiService.query(resource, {})
      .then(({ data }) => {
        const list = data?.data?.items ?? data?.data ?? data;
        return { data: Array.isArray(list) ? list : [] };
      })
      .catch(toApiError);
  }

  @Action
  [Actions.SEARCH_DEEPLINK_MEMBERS](filter: {
    name?: string;
    phone?: string;
    email?: string;
  }): Promise<DeeplinkApiResult<Array<DeeplinkMember>>> {
    ApiService.setAuthorizationHeader();
    return ApiService.query('/members', { params: { ...filter, limit: 20 } })
      .then(({ data }) => ({ data: data.data }))
      .catch(toApiError);
  }

  @Action
  [Actions.SEND_DEEPLINK_NOTIFICATION](
    payload: DeeplinkNotificationPayload
  ): Promise<DeeplinkApiResult<DeeplinkSendResult>> {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/deeplink-notifications/send', payload as never)
      .then(({ data }) => ({ data: data.data ?? data }))
      .catch(toApiError);
  }
}
